import * as React from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { X } from "lucide-react";

/**
 * ShortcutsHelp — global "?" overlay documenting keyboard shortcuts.
 *
 * Mounted in AppShell. Opens on `?` (Shift+/) unless focus is in an
 * input/textarea. Closes on Esc.
 */
const SHORTCUTS = [
  { keys: ["⌘", "K"], desc: "Open command palette" },
  { keys: ["?"],      desc: "Show this help" },
  { keys: ["Esc"],    desc: "Close top-most drawer / dialog / palette" },
  { keys: ["Enter"],  desc: "Submit the focused form" },
  { keys: ["↑", "↓"], desc: "Move through menus, palettes, lists" },
  { keys: ["Tab"],    desc: "Move forward through focusable elements" },
  { keys: ["⇧", "Tab"], desc: "Move backward" },
  { keys: ["G", "→", "D"], desc: "Type G, then the target: D (dashboard), B (bookings), C (comms), R (rotas)…", nested: true },
];

export default function ShortcutsHelp() {
  const [open, setOpen] = React.useState(false);

  React.useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "?" || e.metaKey || e.ctrlKey || e.altKey) return;
      const target = e.target;
      const tag = (target?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || target?.isContentEditable) return;
      e.preventDefault();
      setOpen(true);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-brand-ink/40 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0" />
        <Dialog.Content
          className="fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2 w-[min(560px,92vw)] bg-card border border-brand-rule rounded-xl shadow-xl p-5 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95 data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95"
          data-testid="shortcuts-help"
        >
          <div className="flex items-start justify-between">
            <div>
              <div className="micro-label">Shortcuts</div>
              <Dialog.Title className="mt-0.5 text-[16px] font-semibold text-brand-ink">Keyboard help</Dialog.Title>
            </div>
            <Dialog.Close asChild>
              <button type="button" className="btn-ghost h-8 w-8 !p-0" data-testid="shortcuts-help-close" aria-label="Close">
                <X size={13} />
              </button>
            </Dialog.Close>
          </div>
          <div className="mt-4 divide-y divide-brand-rule">
            {SHORTCUTS.map((s, i) => (
              <div key={i} className="flex items-center gap-3 py-2.5">
                <div className="flex items-center gap-1 min-w-[110px]">
                  {s.keys.map((k, j) => (
                    <kbd key={j} className="mono-num text-[11px] px-1.5 py-0.5 rounded bg-brand-canvas border border-brand-rule text-brand-ink shadow-[0_1px_0_rgba(27,19,48,0.04)]">
                      {k}
                    </kbd>
                  ))}
                </div>
                <div className={`text-[12.5px] ${s.nested ? "text-brand-muted" : "text-brand-ink"}`}>
                  {s.desc}
                </div>
              </div>
            ))}
          </div>
          <div className="mt-4 text-[11.5px] text-brand-muted">
            Press <kbd className="mono-num text-[10px] px-1 rounded bg-brand-canvas border border-brand-rule">?</kbd> any time.
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
